import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { reportsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { Card, CardContent } from '../../../components/ui/Card';
import { ArrowLeft } from 'lucide-react';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const toInputDate = (d) => new Date(d).toISOString().split('T')[0];
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '';

export default function SegmentedProfitAndLoss() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => new Date(today.getFullYear(), 0, 1), [today]);

  const [period, setPeriod] = useState({
    start_date: toInputDate(defaultStart),
    end_date: toInputDate(today),
    period: 'monthly',
  });

  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await reportsAPI.getSegmentedPL({ ...period });
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load Segmented P&L');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/reports')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Segmented Profit & Loss</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">P&L by period breakdown</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">{error}</div>
        )}

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <FormLabel htmlFor="start-date">Start Date</FormLabel>
                <Input
                  id="start-date"
                  type="date"
                  value={period.start_date}
                  onChange={(e) => setPeriod((p) => ({ ...p, start_date: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div>
                <FormLabel htmlFor="end-date">End Date</FormLabel>
                <Input
                  id="end-date"
                  type="date"
                  value={period.end_date}
                  onChange={(e) => setPeriod((p) => ({ ...p, end_date: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div>
                <FormLabel htmlFor="period">Period</FormLabel>
                <Select
                  id="period"
                  value={period.period}
                  onChange={(e) => setPeriod((p) => ({ ...p, period: e.target.value }))}
                  className="h-8"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </Select>
              </div>
              <div className="flex items-end">
                <Button variant="primary" size="sm" onClick={handleApplyFilters} className="w-full">Apply</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading...</p>
            </div>
          ) : data && (
            <CardContent className="p-6">
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead align="left">Period</TableHead>
                    <TableHead align="right">Sales</TableHead>
                    <TableHead align="right">Purchases</TableHead>
                    <TableHead align="right">Direct Expenses</TableHead>
                    <TableHead align="right">Overheads</TableHead>
                    <TableHead align="right">Gross Profit</TableHead>
                    <TableHead align="right">Net Profit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.segments && data.segments.map((segment, idx) => (
                    <TableRow key={idx}>
                      <TableCell align="left">{formatDate(segment.periodStart)}</TableCell>
                      <TableCell align="right" className="tabular-nums">{currency(segment.sales || 0)}</TableCell>
                      <TableCell align="right" className="tabular-nums">{currency(segment.purchases || 0)}</TableCell>
                      <TableCell align="right" className="tabular-nums">{currency(segment.directExpenses || 0)}</TableCell>
                      <TableCell align="right" className="tabular-nums">{currency(segment.overheads || 0)}</TableCell>
                      <TableCell align="right" className="tabular-nums font-semibold">{currency(segment.grossProfit || 0)}</TableCell>
                      <TableCell align="right" className="tabular-nums font-semibold">{currency(segment.netProfit || 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
